import {
  Stack,
  Title,
  Text,
  Button,
  Group,
  SimpleGrid,
  Skeleton,
  Card,
  Paper,
  ActionIcon,
  Pagination,
  Select,
} from "@mantine/core";
import { IconStopwatch, IconPlus, IconChevronLeft, IconArrowUp, IconArrowDown } from "@tabler/icons-react";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useQuickBites } from "../../hooks/useQuickBites";
import { QuickBiteDrawer } from "./QuickBiteDrawer";
import { QuickBiteRecipeCard } from "./QuickBiteRecipeCard";
import { Recipe } from "../../types/mealPlan";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { notifications } from "@mantine/notifications";
import { ContextualFeedbackModal } from "../Support/ContextualFeedbackModal";
import { NutritionDrawer } from "../Recipe/NutritionDrawer";
import { buildRecipePath } from '../../utils/routeHelpers';
import { householdSegment, HouseholdSegment } from '../../utils/householdSegment';
import { getPageCache, setPageCache } from '../../utils/pageCache';
import { useAppUser } from '../../hooks/useAppUser';
import { formatCount } from '../../utils/formatCount';

export const QuickBitesView = () => {
  const { user, loading: authLoading } = useAuth();
  const { user: appUser, profileLoaded } = useAppUser();
  const { fetchQuickBites } = useQuickBites();
  // Seed from the recipes this session last displayed, so a back navigation
  // renders them on the first paint and the mount fetch refreshes silently.
  const cacheKey = user ? `quick-bites:${user.uid}` : null;
  const seededQuickBites = getPageCache<Recipe[]>(cacheKey);
  const [quickBites, setQuickBites] = useState<Recipe[]>(seededQuickBites ?? []);
  const [drawerOpened, setDrawerOpened] = useState(false);
  const navigate = useNavigate();
  const [initialLoad, setInitialLoad] = useState(!seededQuickBites);
  // Dietary/household context comes off the merged app user's profile —
  // session-cached on remounts, refreshed by useAppUser. null = not known
  // yet (still loading), [] = known to have none.
  const userDietaryRestrictions: string[] | null = authLoading
    ? null
    : !user
      ? []
      : appUser?.familyProfile
        ? appUser.familyProfile.dietaryRestrictions || []
        : profileLoaded
          ? []
          : null;
  const userHouseholdSegment: HouseholdSegment | undefined = householdSegment(
    appUser?.familyProfile
  );
  const hasQuickBitesDataRef = useRef(!!seededQuickBites);

  // Sorting state
  const [sortField, setSortField] = useState<string>('created');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const recipesPerPage = 12;
  const firstRecipeRef = useRef<HTMLDivElement>(null);
  const hasInitiallyLoadedRef = useRef(false);

  // Feedback modal state
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackContext, setFeedbackContext] = useState<{
    type: 'meal_plan' | 'assignment' | 'quick_bite';
    mealPlanId?: string;
    assignmentId?: string;
    recipeName?: string;
    recipeId?: string;
  } | null>(null);

  // Nutrition drawer state
  const [nutritionDrawerOpened, setNutritionDrawerOpened] = useState(false);
  const [selectedRecipeForNutrition, setSelectedRecipeForNutrition] = useState<Recipe | null>(null);

  const loadQuickBites = useCallback(async () => {
    if (!user || authLoading) return;
    const recipes = await fetchQuickBites();
    hasQuickBitesDataRef.current = true;
    setQuickBites(recipes);
    setInitialLoad(false);
  }, [user, authLoading, fetchQuickBites]);

  useEffect(() => {
    loadQuickBites();
  }, [loadQuickBites]);

  // Mirror displayed recipes into the session cache so local mutations
  // (newly generated bites, optimistic deletes) are what a back navigation
  // resumes from.
  useEffect(() => {
    if (hasQuickBitesDataRef.current) {
      setPageCache(cacheKey, quickBites);
    }
  }, [quickBites, cacheKey]);

  const handleRecipeGenerated = (recipe: Recipe | Recipe[]) => {
    // Add new recipe(s) to the list
    const newRecipes = Array.isArray(recipe) ? recipe : [recipe];
    setQuickBites((prev) => [...newRecipes, ...prev]);
  };

  const handleRecipeClick = (recipe: Recipe) => {
    if (user) {
      navigate(buildRecipePath(recipe, user.uid));
    }
  };

  const handleDeleteRecipe = async (recipe: Recipe) => {
    if (!user) return;

    const notificationId = `delete-${recipe.id}`;

    // Optimistically remove from UI
    setQuickBites((prev) => prev.filter((r) => r.id !== recipe.id));

    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/recipes/id/${recipe.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        // Create undo button component with loading state
        const UndoButton = () => {
          const [isUndoing, setIsUndoing] = useState(false);

          return (
            <Button
              size="xs"
              variant="subtle"
              color="orange"
              loading={isUndoing}
              onClick={async () => {
                setIsUndoing(true);
                try {
                  await handleUndoDelete(recipe, notificationId);
                } finally {
                  setIsUndoing(false);
                }
              }}
            >
              Undo
            </Button>
          );
        };

        // Show notification with undo option
        notifications.show({
          id: notificationId,
          title: "Recipe deleted",
          message: (
            <Group>
              <Text size="sm">{`"${recipe.name}" has been deleted`}</Text>
              <UndoButton />
            </Group>
          ),
          color: "orange",
          autoClose: 5000,
          withCloseButton: true,
        });
      } else {
        // If API call failed, restore the recipe
        setQuickBites((prev) => [...prev, recipe]);
        notifications.show({
          title: "Error",
          message: "Failed to delete recipe. Please try again.",
          color: "red",
        });
      }
    } catch (error) {
      // If API call errored, restore the recipe
      setQuickBites((prev) => [...prev, recipe]);
      notifications.show({
        title: "Error",
        message: "Failed to delete recipe. Please try again.",
        color: "red",
      });
    }
  };

  const handleUndoDelete = async (recipe: Recipe, notificationId: string) => {
    if (!user) return;

    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/recipes/id/${recipe.id}/undo`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        // Add recipe back to the list
        setQuickBites((prev) => {
          const exists = prev.some((r) => r.id === recipe.id);
          return exists ? prev : [recipe, ...prev];
        });
        notifications.hide(notificationId);
      } else {
        notifications.show({
          title: "Error",
          message: "Failed to restore recipe",
          color: "red",
        });
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to restore recipe",
        color: "red",
      });
    }
  };

  const handleToggleCookbook = async (recipe: Recipe) => {
    if (!user) return;

    const isAdding = !recipe.isSaved;

    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/recipes/cookbook/${recipe.id}`,
        {
          method: isAdding ? "POST" : "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        // Update UI after API success
        setQuickBites((prev) =>
          prev.map((r) =>
            r.id === recipe.id ? { ...r, isSaved: isAdding } : r
          )
        );
        notifications.show({
          title: isAdding ? "Added to Cookbook" : "Removed from Cookbook",
          message: `"${recipe.name}" has been ${isAdding ? "added to" : "removed from"} your cookbook`,
          color: isAdding ? "green" : "orange",
        });
      } else {
        notifications.show({
          title: "Error",
          message: `Failed to ${isAdding ? "add to" : "remove from"} cookbook`,
          color: "red",
        });
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: `Failed to ${isAdding ? "add to" : "remove from"} cookbook`,
        color: "red",
      });
    }
  };

  const handleRecipeFeedback = (recipe: Recipe) => {
    setFeedbackContext({
      type: 'quick_bite',
      recipeId: recipe.id,
      recipeName: recipe.name,
    });
    setTimeout(() => setFeedbackModalOpen(true), 0);
  };

  const handleShowNutrition = (recipe: Recipe) => {
    setSelectedRecipeForNutrition(recipe);
    setTimeout(() => setNutritionDrawerOpened(true), 0);
  };

  // Sort recipes
  const sortedRecipes = useMemo(() => {
    const sorted = [...quickBites].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'created':
          aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
        case 'totalTime':
          aValue = a.totalTimeMinutes;
          bValue = b.totalTimeMinutes;
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [quickBites, sortField, sortDirection]);

  // Calculate pagination
  const totalPages = Math.ceil(sortedRecipes.length / recipesPerPage);
  const startIndex = (currentPage - 1) * recipesPerPage;
  const endIndex = startIndex + recipesPerPage;
  const currentRecipes = sortedRecipes.slice(startIndex, endIndex);

  // Scroll to top when page changes (but not on initial load)
  useEffect(() => {
    if (firstRecipeRef.current && currentRecipes.length > 0 && hasInitiallyLoadedRef.current) {
      // Use requestAnimationFrame for better timing with DOM updates
      requestAnimationFrame(() => {
        firstRecipeRef.current?.scrollIntoView({
          behavior: 'auto',
          block: 'center' // Use center to avoid being cut off by the navbar
        });
      });
    }
    // Mark as initially loaded after first render
    if (currentRecipes.length > 0 && !hasInitiallyLoadedRef.current) {
      hasInitiallyLoadedRef.current = true;
    }
  }, [currentPage, currentRecipes.length]);

  return (
    <>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => navigate("/")}
              size="lg"
            >
              <IconChevronLeft size={20} />
            </ActionIcon>
            <Stack gap={0} className="page-header" style={{ minWidth: 0 }}>
              <Title order={2}>Quick bites</Title>
              <Text c="dimmed" size="sm" className="page-subtitle">
                Easy meals in 30 minutes or less
              </Text>
            </Stack>
          </Group>
          {/* Mobile: Icon button */}
          <ActionIcon
            variant="filled"
            size="lg"
            onClick={() => setDrawerOpened(true)}
            hiddenFrom="sm"
          >
            <IconPlus size={20} />
          </ActionIcon>
          {/* Desktop: Full button */}
          <Button
            leftSection={<IconPlus size={18} />}
            onClick={() => setDrawerOpened(true)}
            visibleFrom="sm"
          >
            New quick bite
          </Button>
        </Group>

        {/* Sort Options */}
        {initialLoad ? (
          <Group justify="space-between" align="center">
            <Skeleton height={20} width="min(280px, 85%)" />
            <Group gap="xs">
              <Skeleton height={36} width={140} radius="md" />
              <Skeleton height={28} width={28} radius="md" />
            </Group>
          </Group>
        ) : quickBites.length > 0 ? (
          <Group justify="space-between" align="center">
            <Text fw={500}>
              {formatCount(sortedRecipes.length)} recipe{sortedRecipes.length !== 1 ? 's' : ''} found
              {sortedRecipes.length > recipesPerPage && (
                <Text component="span" c="dimmed" size="sm">
                  {' '}• Showing {formatCount(startIndex + 1)}-{formatCount(Math.min(endIndex, sortedRecipes.length))} of {formatCount(sortedRecipes.length)}
                </Text>
              )}
            </Text>
            <Group gap="xs">
              <Select
                placeholder="Sort by"
                value={sortField}
                onChange={(value) => setSortField(value || 'created')}
                data={[
                  { value: 'created', label: 'Date added' },
                  { value: 'name', label: 'Name' },
                  { value: 'totalTime', label: 'Total time' },
                ]}
                size="sm"
                w={140}
              />
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                title={`Sort ${sortDirection === 'asc' ? 'Descending' : 'Ascending'}`}
              >
                {sortDirection === 'asc' ? <IconArrowUp size={16} /> : <IconArrowDown size={16} />}
              </ActionIcon>
            </Group>
          </Group>
        ) : null}

        {/* Content */}
        {initialLoad ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} withBorder radius="lg">
                <Stack gap="md">
                  <Skeleton height={200} radius="md" />
                  <Stack gap="sm">
                    <Skeleton height={22} width="75%" />
                    <Skeleton height={14} width="100%" />
                    <Skeleton height={14} width="60%" />
                    <Group gap="xs">
                      <Skeleton height={14} width={70} />
                      <Skeleton height={14} width={80} />
                      <Skeleton height={18} width={50} radius="xl" />
                    </Group>
                    <Group gap="xs">
                      <Skeleton height={14} width={90} />
                      <Skeleton height={14} width={60} />
                    </Group>
                    <Group gap="xs">
                      <Skeleton height={18} width={60} radius="xl" />
                      <Skeleton height={18} width={70} radius="xl" />
                    </Group>
                  </Stack>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        ) : quickBites.length === 0 ? (
          <Paper p="xl" radius="lg" withBorder>
            <Stack align="center" gap="md">
              <IconStopwatch size={64} stroke={1.5} color="gray" />
              <Title order={3} ta="center">
                No quick bites yet
              </Title>
              <Text ta="center" c="dimmed" maw={400}>
                Create your first quick bite recipe! Perfect for when you need a
                fast meal with ingredients on hand.
              </Text>
              <Button
                leftSection={<IconPlus size={18} />}
                onClick={() => setDrawerOpened(true)}
                mt="md"
              >
                Create your first quick bite
              </Button>
            </Stack>
          </Paper>
        ) : (
          <>
            <SimpleGrid
              cols={{ base: 1, sm: 2, md: 3 }}
              spacing="lg"
            >
              {currentRecipes.map((recipe, index) => (
                <div key={recipe.id} ref={index === 0 ? firstRecipeRef : null}>
                  <QuickBiteRecipeCard
                    recipe={recipe}
                    onRecipeClick={handleRecipeClick}
                    onDelete={handleDeleteRecipe}
                    onToggleCookbook={handleToggleCookbook}
                    onSendFeedback={handleRecipeFeedback}
                    onShowNutrition={handleShowNutrition}
                    maxTags={2}
                    userDietaryRestrictions={userDietaryRestrictions}
                    userHouseholdSegment={userHouseholdSegment}
                    radius="lg"
                  />
                </div>
              ))}
            </SimpleGrid>

            {/* Pagination */}
            {totalPages > 1 && (
              <Group justify="center" mt="xl" mb="md">
                <Pagination
                  total={totalPages}
                  value={currentPage}
                  onChange={setCurrentPage}
                  size="md"
                  siblings={0}
                />
              </Group>
            )}
          </>
        )}
      </Stack>

      {/* Drawer */}
      <QuickBiteDrawer
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        onRecipeGenerated={handleRecipeGenerated}
        userDietaryRestrictions={userDietaryRestrictions}
        userHouseholdSegment={userHouseholdSegment}
      />

      {/* Feedback Modal */}
      {feedbackContext && (
        <ContextualFeedbackModal
          opened={feedbackModalOpen}
          onClose={() => {
            setFeedbackModalOpen(false);
            setTimeout(() => setFeedbackContext(null), 300);
          }}
          context={feedbackContext}
        />
      )}

      {/* Nutrition Drawer */}
      {selectedRecipeForNutrition?.nutrition && (
        <NutritionDrawer
          opened={nutritionDrawerOpened}
          onClose={() => {
            setNutritionDrawerOpened(false);
            setSelectedRecipeForNutrition(null);
          }}
          nutrition={selectedRecipeForNutrition.nutrition}
          recipeName={selectedRecipeForNutrition.name}
          recipeId={selectedRecipeForNutrition.id}
          ingredientCount={selectedRecipeForNutrition.ingredients?.length}
        />
      )}
    </>
  );
};
